/**
 * Starter Tabs — wraps Mantine's `<Tabs>` with two design-system variants:
 *
 *   - `line` (default): an active-label underline only — no full-width rail.
 *     The 2px ink indicator hugs the active label and eases in on activation.
 *   - `pill`: free-standing button tabs. The selected tab is a raised surface
 *     button (white in light mode) with a hairline border + soft shadow;
 *     unselected tabs are quiet text that pick up a light hover fill.
 *
 * Mantine's `variant="default" | "outline" | "pills"` still pass through
 * for legacy usages — the design variants are an additional layer keyed
 * off `data-design-variant`.
 *
 * Compound parts (List, Tab, Panel) are re-exposed for parity.
 */
import { Tabs as MantineTabs, type TabsProps as MantineTabsProps } from '@mantine/core';
import type { ReactElement } from 'react';
import styles from './Tabs.module.css';

export type DesignTabsVariant = 'line' | 'pill';
type MantineTabsVariant = NonNullable<MantineTabsProps['variant']>;

export type TabsVariant = DesignTabsVariant | MantineTabsVariant;

const DESIGN_VARIANTS = new Set<string>(['line', 'pill']);

export interface TabsProps extends Omit<MantineTabsProps, 'variant'> {
  /**
   * Design-system variant (`line` is the default — an active-label underline
   * with no full-width rail; `pill` gives button-style tabs with a prominent
   * selected button), or Mantine's raw `default`/`outline`/`pills` for one-off
   * needs.
   */
  readonly variant?: TabsVariant;
}

function TabsRoot({
  variant = 'line',
  classNames,
  ...rest
}: TabsProps): ReactElement {
  const isDesignVariant = DESIGN_VARIANTS.has(variant);

  if (!isDesignVariant) {
    return <MantineTabs variant={variant} classNames={classNames as never} {...rest} />;
  }

  const existing = (classNames ?? {}) as {
    root?: string;
    list?: string;
    tab?: string;
    tabLabel?: string;
    panel?: string;
  };
  const mergedClassNames = {
    ...existing,
    root: [styles.root, existing.root].filter(Boolean).join(' '),
    list: [styles.list, existing.list].filter(Boolean).join(' '),
    tab: [styles.tab, existing.tab].filter(Boolean).join(' '),
    tabLabel: [styles.tabLabel, existing.tabLabel].filter(Boolean).join(' '),
    panel: [styles.panel, existing.panel].filter(Boolean).join(' '),
  };

  return (
    <MantineTabs
      variant="default"
      data-design-variant={variant}
      classNames={mergedClassNames}
      {...rest}
    />
  );
}

export const Tabs = Object.assign(TabsRoot, {
  List: MantineTabs.List,
  Tab: MantineTabs.Tab,
  Panel: MantineTabs.Panel,
});
